import { Component, ChangeDetectionStrategy, OnInit, inject } from '@angular/core';
import { UserService } from './user/user.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { combineLatest, filter, map, switchMap, take, tap } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { SystemService } from './system/system.service';
import { TuiPlatform } from '@taiga-ui/cdk/directives/platform';
import { TuiVisualViewport } from '@taiga-ui/cdk/directives/visual-viewport';
import { TuiFontSize } from '@taiga-ui/cdk/directives/font-size';
import { TuiRoot } from '@taiga-ui/core';
import { PageWrapperComponent } from './pages/page-wrapper/page-wrapper.component';
import { RouterOutlet } from '@angular/router';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TuiPlatform, TuiVisualViewport, TuiFontSize, TuiRoot, PageWrapperComponent, RouterOutlet, AsyncPipe]
})
export class AppComponent implements OnInit {
  private readonly _oidcService = inject(OidcSecurityService);
  private readonly _userProfileService = inject(UserService);
  private readonly _translateService = inject(TranslocoService);
  private readonly _systemService = inject(SystemService);

  // withAppInitializerAuthCheck() already ran checkAuth() before this component loads
  loggedIn$ = this._oidcService.isAuthenticated$.pipe(map(({ isAuthenticated }) => isAuthenticated));

  ngOnInit(): void {
    this._oidcService.isAuthenticated$.pipe(
      filter(({ isAuthenticated }) => isAuthenticated),
      take(1),
      tap(() => {
        this._userProfileService.refreshBasicInfo();
        this._userProfileService.refreshCurrentStoryBasicInfo();
      }),
      switchMap(() => {
        return combineLatest([
          this._systemService.getLocales().pipe(map(v => v.data)),
          this._userProfileService.basicInfo$
        ]).pipe(
          map(([locales, user]) => ({ locales, user })),
          filter(v => v.user != null),
          take(1),
          tap(v => {
            const localeId = v.user!.localeId ?? 1;
            const selectedLocale = v.locales!.find(i => i.id == localeId)!;
            this._translateService.setActiveLang(selectedLocale.languageCode);
            this._systemService.locales$.next(v.locales);
          })
        );
      })
    ).subscribe();
  }
}
